import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../utils/apiClient';
import { Button, Space, message } from 'antd';
import { EditOutlined, LockOutlined, UserAddOutlined } from '@ant-design/icons';
import { TGDataTable } from '../common';
import EditUserDialog from '../EditUserDialog';
import ChangePasswordDialog from '../ChangePasswordDialog';
import AddUserDialog from './AddUserDialog';
import LastLoginFilterBar from './LastLoginFilterBar';
import { useUser } from '../../contexts/UserContext';

function Users() {
  const [rowData, setRowData] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [selectedUserForPasswordChange, setSelectedUserForPasswordChange] = useState(null);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const { roles, privileges } = useUser();

  const handleChangePassword = (user) => {
    setSelectedUserForPasswordChange(user);
    setShowChangePasswordDialog(true);
  };

  const submitNewPassword = (passwordData) => {
    api.post('/change_password', passwordData)
      .then(response => {
        setShowChangePasswordDialog(false);
        message.success("Password changed successfully!");
      })
      .catch(error => {
        console.error('Error changing password:', error);
        message.error("Failed to change password");
      });
  };

  const handleEdit = (user) => {
    const [first_name, ...last_name] = user.name.split(' ');
    const editingUser = {
      ...user,
      first_name,
      last_name: last_name.join(' ')
    };
    setEditingUser(editingUser);
    setShowEditDialog(true);
  };

  const submitEdit = (updatedUser) => {
    api.post('/update_user', updatedUser)
      .then(response => {
        message.success("Changes saved successfully!");
        setShowEditDialog(false);
        fetchData();
      })
      .catch(error => {
        console.error('Error updating user:', error);
        message.error("Failed to update user");
      });
  };

  const fetchData = () => {
    setLoading(true);
    api.get('/users')
      .then(response => {
        const usersData = response.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          lastLogin: user.last_login,
          failedLoginAttempts: user.failed_login_attempts,
          locked: user.locked ? 'Yes' : 'No',
          active: user.active ? 'Yes' : 'No',
          org_id: user.org_id,
          role: user.roles.map(role => role.name).join(', '),
        }));
        setRowData(usersData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching users:', error);
        setLoading(false);
      });
  };

  useEffect(fetchData, []);

  // Filter users by last login date
  const filteredData = useMemo(() => {
    if (!dateFilter || !dateFilter.date) return rowData;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return rowData.filter((user) => {
      if (!user.lastLogin || user.lastLogin === 'Never') return false;
      const loginTime = new Date(user.lastLogin).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return loginTime === filterTime;
        case 'after':
          return loginTime > filterTime;
        case 'before':
          return loginTime < filterTime;
        default:
          return true;
      }
    });
  }, [rowData, dateFilter]);

  // Column definitions for TGDataTable
  const columns = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      fixed: 'left',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 250,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'role',
      title: 'Roles',
      dataIndex: 'role',
      width: 200,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'lastLogin',
      title: 'Last Login',
      dataIndex: 'lastLogin',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString() : 'Never',
      sortable: true,
    },
    {
      key: 'failedLoginAttempts',
      title: 'Failed Attempts',
      dataIndex: 'failedLoginAttempts',
      width: 140,
      align: 'center',
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'locked',
      title: 'Locked',
      dataIndex: 'locked',
      width: 100,
      align: 'center',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
    },
    {
      key: 'active',
      title: 'Active',
      dataIndex: 'active',
      width: 100,
      align: 'center',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit User"
          />
          <Button
            size="small"
            icon={<LockOutlined />}
            onClick={() => handleChangePassword(record)}
            title="Change Password"
          />
        </Space>
      ),
    },
  ];

  // Last Login filter toolbar
  const toolbar = (
    <LastLoginFilterBar
      dateFilter={dateFilter}
      onDateFilterChange={setDateFilter}
    />
  );

  const handleClearFilters = () => {
    setDateFilter(null);
  };

  const externalFilterCount = dateFilter && dateFilter.date ? 1 : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => setShowAddUserDialog(true)}
          size="large"
        >
          Add User
        </Button>
      </div>

      <TGDataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination="client"
        pageSize={10}
        showExport
        showColumnConfig
        exportFilename="users"
        presetKey="admin.users"
        toolbar={toolbar}
        externalFilterCount={externalFilterCount}
        onClearExternalFilters={handleClearFilters}
      />

      {showEditDialog && (
        <EditUserDialog
          key={editingUser ? editingUser.id : null}
          user={editingUser}
          onSubmit={submitEdit}
          onCancel={() => setShowEditDialog(false)}
        />
      )}
      {showChangePasswordDialog && (
        <ChangePasswordDialog
          user={selectedUserForPasswordChange}
          onSubmit={submitNewPassword}
          onCancel={() => setShowChangePasswordDialog(false)}
        />
      )}
      {showAddUserDialog && (
        <AddUserDialog
          open={showAddUserDialog}
          onClose={() => setShowAddUserDialog(false)}
          onUserAdded={fetchData}
        />
      )}
    </div>
  );
}

export default Users;
